import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaArrowRight,
  FaCalendarAlt,
  FaChevronDown,
  FaClock,
  FaGamepad,
  FaMapMarkerAlt,
  FaRoad,
  FaSearch,
  FaServer,
  FaStar,
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

const INITIAL_EVENTS_COUNT = 12;
const EVENTS_PAGE_SIZE = 12;
const FALLBACK_BANNER = 'https://hub.mileageriot.com/assets/images/banner/banner.jpg';

const Events = () => {
  const [eventsData, setEventsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [gameFilter, setGameFilter] = useState('all');
  const [visibleCount, setVisibleCount] = useState(INITIAL_EVENTS_COUNT);
  const { t, i18n } = useTranslation();
  const eventsRef = useRef();

  const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-GB' : 'ru-RU';

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('animate-in');
        });
      },
      { threshold: 0.08 }
    );

    if (eventsRef.current) observer.observe(eventsRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        const cachedData = localStorage.getItem('eventsData');
        const cachedTimestamp = Number(localStorage.getItem('eventsDataTimestamp'));
        const isCacheValid = cachedTimestamp && Date.now() - cachedTimestamp < 60 * 60 * 1000;

        let events;
        if (cachedData && isCacheValid) {
          events = JSON.parse(cachedData);
        } else {
          const response = await fetch('https://hub.mileageriot.com/api/events');
          if (!response.ok) throw new Error(`Events request failed: ${response.status}`);
          events = await response.json();
          localStorage.setItem('eventsData', JSON.stringify(events));
          localStorage.setItem('eventsDataTimestamp', Date.now().toString());
        }

        if (!Array.isArray(events)) throw new Error('Events response is not an array');

        const sortedEvents = [...events].sort((a, b) => {
          const aDate = new Date(`${a.date}T${a.departure_time || '00:00:00'}Z`).getTime();
          const bDate = new Date(`${b.date}T${b.departure_time || '00:00:00'}Z`).getTime();
          return aDate - bDate;
        });

        setEventsData(sortedEvents);
      } catch (error) {
        console.error('Error fetching Events data:', error);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, []);

  useEffect(() => {
    setVisibleCount(INITIAL_EVENTS_COUNT);
  }, [searchQuery, gameFilter]);

  const formatDate = (dateString, options = {}) => {
    const date = new Date(`${dateString}T12:00:00Z`);
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: options.long ? 'long' : 'short',
      year: 'numeric',
    }).format(date);
  };

  const formatTime = (time) => (time ? time.slice(0, 5) : '—');

  const filteredEvents = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLocaleLowerCase(locale);

    return eventsData.filter((event) => {
      const matchesGame = gameFilter === 'all' || Number(event.game) === Number(gameFilter);
      if (!matchesGame) return false;
      if (!normalizedQuery) return true;

      const searchable = [
        event.title,
        event.start_location,
        event.end_location,
        event.server,
        event.dlc,
      ]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase(locale);

      return searchable.includes(normalizedQuery);
    });
  }, [eventsData, gameFilter, locale, searchQuery]);

  const visibleEvents = filteredEvents.slice(0, visibleCount);
  const nextEvent = eventsData[0];

  const handleImageError = (event) => {
    event.currentTarget.onerror = null;
    event.currentTarget.src = FALLBACK_BANNER;
  };

  return (
    <>
      <Helmet>
        <title>Конвои и события — Mileage Riot ВТК | ETS2 & ATS</title>
        <meta
          name="description"
          content="Расписание конвоев и событий Mileage Riot — присоединяйся к конвоям в ETS2 и ATS на TruckersMP."
        />
        <link rel="canonical" href="https://mileageriot.com/events" />
        <meta property="og:title" content="Конвои — Mileage Riot" />
        <meta property="og:url" content="https://mileageriot.com/events" />
        <meta property="og:locale" content={locale === 'en-GB' ? 'en_GB' : 'ru_RU'} />
      </Helmet>

      <section className="events-modern page-events events-upgraded" ref={eventsRef}>
        <div className="events-grid-background" aria-hidden="true"></div>
        <div className="events-glow events-glow-left" aria-hidden="true"></div>
        <div className="events-glow events-glow-right" aria-hidden="true"></div>

        <div className="container events-page-container">
          <header className="events-hero fade-in-up stagger-1">
            <div className="events-hero-copy">
              <div className="section-badge events-badge">
                <FaStar />
                <span>{t('events.badge')}</span>
              </div>
              <h1>{t('events.title')}</h1>
              <p>{t('events.subtitle')}</p>
            </div>

            <div className="events-hero-stats" aria-label={t('events.scheduleSummary')}>
              <div className="events-hero-stat">
                <span>{t('events.upcoming')}</span>
                <strong>{eventsData.length || '—'}</strong>
              </div>
              <div className="events-hero-stat events-hero-stat-next">
                <span>{t('events.nextConvoy')}</span>
                <strong>{nextEvent ? formatDate(nextEvent.date) : '—'}</strong>
              </div>
            </div>
          </header>

          <div className="events-toolbar fade-in-up stagger-2">
            <label className="events-search">
              <FaSearch aria-hidden="true" />
              <span className="visually-hidden">{t('events.searchLabel')}</span>
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={t('events.searchPlaceholder')}
              />
            </label>

            <div className="events-game-filters" role="group" aria-label={t('events.gameFilter')}>
              {[
                { value: 'all', label: t('events.allGames') },
                { value: '0', label: 'ETS2' },
                { value: '1', label: 'ATS' },
              ].map((filter) => (
                <button
                  type="button"
                  key={filter.value}
                  className={gameFilter === filter.value ? 'active' : ''}
                  onClick={() => setGameFilter(filter.value)}
                  aria-pressed={gameFilter === filter.value}
                >
                  {filter.value !== 'all' && <FaGamepad aria-hidden="true" />}
                  {filter.label}
                </button>
              ))}
            </div>

            <span className="events-results-count">
              {t('events.found', { count: filteredEvents.length })}
            </span>
          </div>

          {isLoading ? (
            <div className="events-state" role="status">
              <span className="events-loader" aria-hidden="true"></span>
              <p>{t('events.loadingList')}</p>
            </div>
          ) : hasError ? (
            <div className="events-state events-state-error" role="alert">
              <FaCalendarAlt aria-hidden="true" />
              <h2>{t('events.error')}</h2>
              <p>{t('events.errorHint')}</p>
            </div>
          ) : visibleEvents.length === 0 ? (
            <div className="events-state">
              <FaSearch aria-hidden="true" />
              <h2>{t('events.noResults')}</h2>
              <p>{t('events.noResultsHint')}</p>
            </div>
          ) : (
            <>
              <div className="events-list" aria-live="polite">
                {visibleEvents.map((event, index) => (
                  <article className="event-card-upgraded fade-in-up" key={event.id}>
                    <Link className="event-card-link" to={`/events/${event.id}`} aria-label={`${event.title} — ${t('events.viewDetails')}`}>
                      <div className="event-card-media">
                        <img
                          loading={index < 4 ? 'eager' : 'lazy'}
                          decoding="async"
                          src={event.banner || FALLBACK_BANNER}
                          alt=""
                          onError={handleImageError}
                        />
                        <div className="event-card-media-shade"></div>
                        <div className="event-card-date">
                          <FaCalendarAlt aria-hidden="true" />
                          <span>{formatDate(event.date, { long: true })}</span>
                        </div>
                        <span className={`event-game-badge event-game-${Number(event.game) === 1 ? 'ats' : 'ets2'}`}>
                          {Number(event.game) === 1 ? 'ATS' : 'ETS2'}
                        </span>
                      </div>

                      <div className="event-card-content">
                        <div className="event-card-heading">
                          <h2>{event.title}</h2>
                          <span className="event-card-arrow" aria-hidden="true"><FaArrowRight /></span>
                        </div>

                        <div className="event-time-row">
                          <div>
                            <span>{t('events.eventDetails.parkingTime')}</span>
                            <strong><FaClock aria-hidden="true" /> {formatTime(event.meetup_time)} UTC</strong>
                          </div>
                          <div>
                            <span>{t('events.eventDetails.departureTime')}</span>
                            <strong><FaArrowRight aria-hidden="true" /> {formatTime(event.departure_time)} UTC</strong>
                          </div>
                        </div>

                        <div className="event-route">
                          <div className="event-route-point">
                            <span className="event-route-dot"></span>
                            <div>
                              <small>{t('events.from')}</small>
                              <strong>{event.start_location || '—'}</strong>
                            </div>
                          </div>
                          <div className="event-route-line" aria-hidden="true"></div>
                          <div className="event-route-point event-route-destination">
                            <span className="event-route-dot"></span>
                            <div>
                              <small>{t('events.to')}</small>
                              <strong>{event.end_location || '—'}</strong>
                            </div>
                          </div>
                        </div>

                        <div className="event-meta-row">
                          <span title={t('events.eventDetails.distance')}><FaRoad /> {event.distance || '—'} KM</span>
                          <span title={t('events.eventDetails.server')}><FaServer /> {event.server || '—'}</span>
                          <span title={t('events.eventDetails.dlc')}><FaMapMarkerAlt /> {event.dlc || t('events.noDlc')}</span>
                        </div>

                        <div className="event-card-cta">
                          <span>{t('events.viewDetails')}</span>
                          <FaArrowRight aria-hidden="true" />
                        </div>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>

              {visibleCount < filteredEvents.length && (
                <div className="events-load-more-wrap">
                  <button
                    type="button"
                    className="events-load-more"
                    onClick={() => setVisibleCount((count) => count + EVENTS_PAGE_SIZE)}
                  >
                    <FaChevronDown aria-hidden="true" />
                    {t('events.showMore')}
                    <span>{visibleCount} / {filteredEvents.length}</span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default Events;
